import { useCallback, useEffect, useRef, useState } from 'react';
import { STATUS_LABELS } from '../constants/statuses';
import { getOrderNumber } from '../utils/orderTitle';

/**
 * THE ROW YOU ARE TICKING STAYS OPEN — owner, 2026-10-28, from his phone on
 * staging: *"on mobile i ticked 1 item as deliverd and the order closed. guess
 * it jusmped to the deliverd page? … it should have stayed open as I wanted to
 * tick of multiple deliveries … especialy for the store man who will have to be
 * ticking lots of items off efficiantly"*.
 *
 * A received/ordered tick changes the order's status SERVER-side (`ordered` →
 * `partially_delivered` → `delivered`), the tick's own list refetch lands, and
 * the row no longer belongs to the tab the user is standing on — so it was
 * filtered away, unmounted, and its expansion lost under the user's thumb.
 * Department Orders has held the row since 2026-09-12 (*"the order close as
 * soon as its ticked"*); My Orders never did — its `On order` → `Deliveries`
 * move is exactly Pete's "jumped to the delivered page" — and Department
 * Orders' pin died on the next 30 s poll, i.e. part-way through a storeman's
 * run of ticks. ONE hook now, so the two pages cannot drift again.
 *
 * THE RULE:
 * - `holdThrough(orderId, refetch)` is the tick's list refresh. On a filtered
 *   tab it HOLDS the row, runs `refetch()` (which must resolve to the new rows,
 *   or `null` on failure), and — if the row's own tab is now a different one —
 *   announces the move ONCE through `announce`: *"ORD-004 partially delivered.
 *   Now in Deliveries."* That toast is the hint; there is no in-row badge
 *   (controller ruling, 2026-10-28).
 * - `heldIds` is ORed into the page's TAB predicate and nowhere else. 🔴 **A
 *   LINGER, NOT A MEMBERSHIP**: the tab counts never read it, so they stay
 *   truthful and a tab may briefly show one more row than it counts.
 * - 🔑 **RELEASED ONLY BY THE USER, NEVER BY A REFETCH OR A TIMER.** The held
 *   set empties when no row is open any more (`releaseWhenAllClosed`, fed the
 *   page's open-row set), when the tab or any other filter in `filterKey`
 *   changes, and on unmount (leaving the page). The 30 s poll, an editor save's
 *   refetch, a retry — none of them release it. Until 2026-10-28 every
 *   Department Orders refetch except the tick's own did, which is what dropped
 *   an open row mid-run.
 * - 🪤 **"No row open", NOT "this row closed".** The order pages keep ONE row
 *   open, so opening row B closes A; releasing A there would pull it out of the
 *   list ABOVE B and jump B under the user's finger. A held, collapsed row stays
 *   (showing its new status) while anything is open, and every held row leaves
 *   together when the user closes the last one.
 * - On `All` (`unfiltered`) nothing can leave, so nothing is held and nothing
 *   is announced — a toast narrating a move the screen did not make is noise.
 * - 🚨 **THE FILTERS ARE CHECKED BEFORE THE HOLD AND AGAIN AFTER THE REFETCH**
 *   (moved verbatim in intent from Department Orders' `stillOnTheSameFilters`).
 *   The tick is a PATCH, a detail GET, then this refetch; a user who changes tab
 *   or search mid-flight gets NO hold and NO toast — the row simply arrives in
 *   its true tab. Without the first check the hold would OR the row into the tab
 *   they just moved TO for the length of one list GET (invisible in jsdom,
 *   which has no gap between commits — measured 2026-09-12 by removing it).
 *
 * Callers: `pages/DepartmentOrders.js`, `pages/MyOrders.js`.
 * 🚫 **Deliberately NOT the hires Table** (`hooks/useHireTableList.js`'s own
 * `stickyIds`): that pin is keyed by `item_id`, held by Extend / Off hire /
 * Bring back rather than by a tick, and consumed by the action's own refetch —
 * a different lifecycle (controller ruling, 2026-10-28). A received tick cannot
 * move a hire between its tabs except by un-ticking a COMPLETED order.
 * Not `withDirtyRowPinned` either: that holds a DIRTY row against PAGINATION.
 *
 * @param {object}   args
 * @param {string}   args.activeTab   the tab the user is standing on
 * @param {boolean}  args.unfiltered  true on `All` — nothing is held
 * @param {Array}    args.filterKey   every OTHER list filter that releases the hold (search, production…)
 * @param {(row: object) => string|null} args.tabOf  the row's own tab id (the toast's destination)
 * @param {Array<{id: string, label: string}>} args.tabs  the page's tab list, for the destination's label
 * @param {(text: string, opts: object) => void} [args.announce]  the info toast (called with `TICK_MOVE_TOAST`)
 * @returns {{ heldIds: Set, holdThrough: Function, releaseWhenAllClosed: Function,
 *   tickBusy: (orderId: number, busy: boolean) => void }}  `tickBusy` → the expansion hook's
 *   `onTickBusyChange`, so the hold STARTS with the tick (see its docblock).
 */
export default function useHeldOpenRows({ activeTab, unfiltered, filterKey, tabOf, tabs, announce }) {
  const [heldIds, setHeldState] = useState(EMPTY_HELD_IDS);
  // EVERY write goes through `setHeldIds`: the ref is the SYNCHRONOUS truth `tickBusy` reads, and
  // React is handed the computed VALUE — so no state updater carries a side effect (re-check Minor 2,
  // 2026-10-29: an updater may run lazily at the next render, and twice under StrictMode).
  const heldRef = useRef(EMPTY_HELD_IDS);
  const setHeldIds = useCallback((next) => {
    const value = typeof next === 'function' ? next(heldRef.current) : next;
    heldRef.current = value;
    setHeldState(value);
  }, []);
  const key = JSON.stringify([activeTab, ...filterKey]);
  // The live key, for a tick to compare its own closure against once its async
  // work has landed — only a ref can answer "has this changed since I started".
  const keyRef = useRef(key);
  // Ids held at TICK START that `holdThrough` has not yet taken over (see `tickBusy`).
  const provisionalRef = useRef(new Set());
  useEffect(() => {
    keyRef.current = key;
    provisionalRef.current.clear();
    setHeldIds(clearHeld);
  }, [key, setHeldIds]);

  // The destination each held row was last announced as moving to.
  const announcedRef = useRef(new Map());
  useEffect(() => {
    if (heldIds.size === 0) announcedRef.current.clear();
  }, [heldIds]);

  const openRef = useRef(EMPTY_HELD_IDS);
  const releaseWhenAllClosed = useCallback((openIds) => {
    openRef.current = openIds || EMPTY_HELD_IDS;
    if (openRef.current.size === 0) {
      provisionalRef.current.clear();
      setHeldIds(clearHeld);
    }
  }, [setHeldIds]);

  /**
   * 🔑 THE HOLD STARTS WITH THE TICK, NOT AFTER IT (review T-1, 2026-10-29 — probed, not
   * reasoned). The tick is a PATCH, a detail GET, then `holdThrough`; the server's status has
   * already moved when the PATCH resolves, so a 30 s poll landing in the detail GET wrote the new
   * status with no hold yet — the row left its tab, UNMOUNTED, and remounted a moment later
   * (measured in `DepartmentOrders.stickyRow.test.js` §2.7 with the GET held pending). Fed the
   * tick cluster's `onBusyChange`: `true` holds the open row PROVISIONALLY; `holdThrough` takes
   * the hold over (or drops it, by its own rules); `false` with the hold still provisional means
   * the tick never reached a refresh (a thrown PATCH) and the hold is released — unless the row
   * was already held by an EARLIER tick, which this tick never owned.
   */
  const tickBusy = useCallback((orderId, busy) => {
    if (orderId == null) return;
    if (busy) {
      if (unfiltered || !openRef.current.has(orderId)) return;
      if (heldRef.current.has(orderId)) return; // an EARLIER tick's hold — never claimed by this one
      provisionalRef.current.add(orderId);
      setHeldIds((prev) => new Set(prev).add(orderId));
      return;
    }
    if (provisionalRef.current.delete(orderId)) setHeldIds((prev) => withoutHeld(prev, orderId));
  }, [unfiltered, setHeldIds]);

  const holdThrough = useCallback(async (orderId, refetch) => {
    const stillOnTheSameFilters = () => keyRef.current === key;
    // This tick's start-hold (if any) is decided HERE from now on, not by `tickBusy(…, false)`.
    const startHeld = provisionalRef.current.delete(orderId);
    // A tick on a row that is no longer open (closed while its PATCH was in
    // flight) holds nothing: there would be no close left to release it.
    if (orderId == null || unfiltered || !stillOnTheSameFilters() || !openRef.current.has(orderId)) {
      if (startHeld) setHeldIds((prev) => withoutHeld(prev, orderId));
      await refetch();
      return;
    }
    setHeldIds((prev) => (prev.has(orderId) ? prev : new Set(prev).add(orderId)));
    const rows = await refetch();
    if (!stillOnTheSameFilters()) {
      setHeldIds(clearHeld);
      return;
    }
    if (!Array.isArray(rows) || !announce) return;
    const row = rows.find((o) => o.id === orderId);
    const toTab = row ? tabOf(row) : null;
    // `null` is an UNRECOGNISED status, not a destination — say nothing rather
    // than invent a second sentence.
    if (!toTab || toTab === activeTab) {
      announcedRef.current.delete(orderId);
      return;
    }
    // ONCE per move, not once per tick: on My Orders the FIRST Delivered tick
    // already moves the row to `Deliveries`, and the storeman's next three
    // ticks must not repeat it.
    if (announcedRef.current.get(orderId) === toTab) return;
    announcedRef.current.set(orderId, toTab);
    const destination = tabs.find((t) => t.id === toTab);
    announce(`${tickMoveSentence(row)} Now in ${destination ? destination.label : toTab}.`, TICK_MOVE_TOAST);
  }, [key, unfiltered, activeTab, tabOf, tabs, announce, setHeldIds]);

  return { heldIds, holdThrough, releaseWhenAllClosed, tickBusy };
}

/**
 * The move toast's placement (owner ruling 3A, 2026-10-29): on a PHONE it sat over the tab strip the
 * user was about to read, so below `md` it goes to the BOTTOM (above the pinned action bar) and is
 * SHORTER; desktop is unchanged. `ToastContext`'s opt-in `phone` option — every other toast keeps
 * today's placement. Exported so the page tests pin the literal the pages actually send.
 */
export const TICK_MOVE_TOAST = Object.freeze({ phone: Object.freeze({ bottom: true, duration: 1500 }) });

/** `prev` without `id` — the SAME reference when there is nothing to remove, EMPTY when it empties. */
function withoutHeld(prev, id) {
  if (!prev.has(id)) return prev;
  const next = new Set(prev);
  next.delete(id);
  return next.size ? next : EMPTY_HELD_IDS;
}

/**
 * The empty held set — ONE instance, so every clear returns the SAME reference
 * (`prev.size ? EMPTY : prev`) and React bails out instead of re-filtering the
 * whole list on every keystroke. Never mutated; the set is only ever REPLACED.
 */
const EMPTY_HELD_IDS = new Set();
const clearHeld = (prev) => (prev.size ? EMPTY_HELD_IDS : prev);

/**
 * The move toast's per-status phrase (2026-09-13, owner ruling; moved here from
 * `DepartmentOrders.js` with the hold, 2026-10-28). Deliberately NOT
 * `STATUS_LABELS`: those are BADGE text — shortened 2026-09-13 to
 * `'Partial delivery'` to save table width — and reading the toast off them
 * printed *"ORD-003 partial delivery. Now in To receive."*, which lower-cases
 * into a fragment rather than a sentence. This map is PROSE, written for the
 * one sentence it appears in, and is free to diverge from the badge wording.
 *
 * Pete: *"correct English please. But if all items are ticked it should say
 * All items delivered."* Only the three statuses a tick can ever auto-advance
 * into are named here:
 *   - `ordered`             — reachable on an UNTICK from `delivered` when
 *                             the order holds exactly one item (0 received
 *                             skips `partially_delivered` entirely), and on the
 *                             per-line Ordered tick that orders the last line.
 *   - `partially_delivered` — reachable both forward (first tick of several)
 *                             and back (untick from `delivered` on a
 *                             multi-item order).
 *   - `delivered`           — the last tick.
 * `delivered`'s sentence has no grammatical subject in common with the order
 * number ("All items delivered" is not something ORD-003 itself did), so it
 * is introduced with a colon: "ORD-003: All items delivered." reads as a label
 * plus a statement, where "ORD-003 all items delivered." would read as a
 * run-on. Any OTHER status falls through to the badge-derived phrase, kept as a
 * safety net so an unrecognised status still produces a sentence — and since
 * the per-line Ordered step (2026-10-28) one more is reachable that way:
 * un-ticking an Ordered line walks a placed order back to `approved`
 * (`server/helpers/deliveryStatus.js#deriveOrderStatus`), "ORD-003 approved.".
 */
const TICK_MOVE_PHRASES = {
  ordered: (orderNumber) => `${orderNumber} ordered.`,
  partially_delivered: (orderNumber) => `${orderNumber} partially delivered.`,
  delivered: (orderNumber) => `${orderNumber}: All items delivered.`,
};

export function tickMoveSentence(row) {
  const build = TICK_MOVE_PHRASES[row.status]
    || ((orderNumber) => `${orderNumber} ${(STATUS_LABELS[row.status] || row.status || '').toLowerCase()}.`);
  return build(getOrderNumber(row));
}
